import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Field } from '@components/dynamic-input-fields/src/dynamic-input-fields.type';
import { FIELD_NUMERIC_LIST } from '@components/dynamic-input-fields/src/utils';
import { ErrorMessage } from '@components/error/src/error-message';
import { Input } from '@components/input';
import { LabelPrimary } from '@components/label';
import { Modal } from '@components/modals/modal/src';
import { ModalStickyFooter } from '@components/modals/modal-sticky-footer';
import { ModalStickyHeader } from '@components/modals/modal-sticky-header';
import { SelectWithOptions } from '@components/select';
import { cannotBeNegative } from '@utils/validations/cannot-be-negative';
import { maxDecimals } from '@utils/validations/max-decimals';
import { min as minValidator } from '@utils/validations/min';

import { DynamicFormFieldForm, ModalDynamicFormFieldProps } from './modal-dynamic-form-field.type';

export const ModalDynamicFormField = ({
  isOpen,
  handleClose,
  onPrimaryAction,
  primaryActionText,
  secondaryActionText,
  title = 'common:addRequirement',
}: ModalDynamicFormFieldProps) => {
  const { t } = useTranslation();
  const methods = useForm<DynamicFormFieldForm>({
    defaultValues: {
      type: 'text',
      name: '',
    },
    mode: 'onChange',
  });
  const {
    watch,
    formState: { errors, isValid },
  } = methods;
  const type = watch('type');
  const name = watch('name');
  const max = watch('max');
  const min = watch('min');
  const createField = (data: DynamicFormFieldForm): Omit<Field, 'id'> => {
    return {
      dataType: data.type,
      name: data.name,
      slug: data.name,
      max: data.max,
      min: data.min,
      isRequired: true,
    };
  };

  const onClick = () => {
    const field = createField({
      type,
      name,
      max,
      min,
    });
    onPrimaryAction(field);
    methods.reset();
  };

  const onClose = () => {
    methods.reset();
    handleClose();
  };

  useEffect(() => {
    if (type !== FIELD_NUMERIC_LIST) {
      methods.resetField('min');
      methods.resetField('max');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  return (
    <Modal isOpen={isOpen} handleClose={onClose}>
      <ModalStickyHeader title={t(title as string)} />
      <FormProvider {...methods}>
        <form>
          <div className='px-6 pb-8 flex flex-col gap-8'>
            <div className='w-full flex flex-col md:flex-row gap-3'>
              <div className='w-full'>
                <LabelPrimary className='text-left' htmlFor='name' hasError={!!errors?.min}>
                  {t('offers:requirementNameLabel')}
                </LabelPrimary>
                <Input
                  {...methods.register('name', {
                    required: t('errors:formFieldRequired') as string,
                  })}
                />
              </div>
              <div className='w-full'>
                <LabelPrimary className='text-left' htmlFor='type' hasError={!!errors?.min}>
                  {t('offers:typeFieldLabel')}
                </LabelPrimary>
                <SelectWithOptions
                  {...methods.register('type', { required: t('errors:formFieldRequired') as string })}
                  options={[
                    { label: t('offers:textFormfieldLabel'), value: 'text' },
                    { label: t('offers:numericFormfieldLabel'), value: FIELD_NUMERIC_LIST },
                  ]}
                />
              </div>
            </div>
            <div className='w-full md:w-1/3 flex flex-row gap-3'>
              {type === FIELD_NUMERIC_LIST && (
                <>
                  <div className='w-full md:shrink-0'>
                    <LabelPrimary className='text-left' htmlFor='min' hasError={!!errors?.min}>
                      {t('offers:minFieldLabel')}
                    </LabelPrimary>
                    <Input
                      id='min'
                      type='number'
                      step={1}
                      min='0'
                      max={max}
                      {...methods.register('min', {
                        required: t('errors:formFieldRequired') as string,
                        validate: {
                          cannotBeNegative: (value) =>
                            cannotBeNegative({
                              value: value?.toString() ?? '',
                              message: t('offers:admissionTicketQuantityNegativeError'),
                            }),
                          maxDecimals: (value) =>
                            maxDecimals({
                              value: value?.toString() ?? '',
                              decimals: 0,
                              message: t('offers:admissionTicketQuantityDecimalsError'),
                            }),
                        },
                      })}
                    />
                    <ErrorMessage name='min' />
                  </div>
                  <div className='w-full md:shrink-0'>
                    <LabelPrimary className='text-left' htmlFor='max' hasError={!!errors?.max}>
                      {t('offers:maxFieldLabel')}
                    </LabelPrimary>
                    <Input
                      id='max'
                      type='number'
                      step={1}
                      min={Number(min) + 1}
                      {...methods.register('max', {
                        required: t('errors:formFieldRequired') as string,
                        validate: {
                          min: (value) =>
                            minValidator({
                              value,
                              minValue: Number(min) + 1,
                              message: t('errors:minQuantityFieldError', { number: Number(min) + 1 }),
                            }),
                          cannotBeNegative: (value) =>
                            cannotBeNegative({
                              value: value?.toString() ?? '',
                              message: t('offers:admissionTicketQuantityNegativeError'),
                            }),
                          maxDecimals: (value) =>
                            maxDecimals({
                              value: value?.toString() ?? '',
                              decimals: 0,
                              message: t('offers:admissionTicketQuantityDecimalsError'),
                            }),
                        },
                      })}
                    />
                    <ErrorMessage name='max' />
                  </div>
                </>
              )}
            </div>
          </div>
          <ModalStickyFooter
            isSubmitDisabled={!isValid}
            primaryButtonText={primaryActionText}
            onPrimaryButtonClick={onClick}
            secondaryButtonText={secondaryActionText}
            onSecondaryButtonClick={handleClose}
          />
        </form>
      </FormProvider>
    </Modal>
  );
};
